/**
 * Visitor and session ids for our own analytics.
 *
 * With analytics consent the visitor id lives in localStorage (survives
 * sessions) and the session id in sessionStorage. Without it both live only in
 * memory for this page load: nothing is written to the device (PECR). When the
 * visitor later allows analytics, the in-memory ids are persisted.
 */
import { hasAnalyticsConsent, onConsentChange } from "@/lib/consent";

const STORAGE_KEY = "cvn_visitor_id";
const SESSION_KEY = "cvn_session_id";

function generateUUID(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

let memVisitorId: string | null = null;
let memSessionId: string | null = null;

export function getVisitorId(): string {
  try {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing) return existing;
  } catch {
    /* storage unavailable */
  }
  if (!memVisitorId) memVisitorId = generateUUID();
  if (hasAnalyticsConsent()) {
    try {
      localStorage.setItem(STORAGE_KEY, memVisitorId);
    } catch {
      /* private browsing etc. */
    }
  }
  return memVisitorId;
}

export function setVisitorId(id: string): void {
  memVisitorId = id;
  if (!hasAnalyticsConsent()) return;
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // ignore
  }
}

/**
 * Session ID — stored in sessionStorage, unique per browser tab/session.
 * Resets when the tab is closed, giving accurate session counts.
 */
export function getSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
  } catch {
    /* storage unavailable */
  }
  if (!memSessionId) memSessionId = `sess-${generateUUID()}`;
  if (hasAnalyticsConsent()) {
    try {
      sessionStorage.setItem(SESSION_KEY, memSessionId);
    } catch {
      /* ignore */
    }
  }
  return memSessionId;
}

/** Withdrawn consent removes the stored ids; granted consent persists the in-memory ones. */
onConsentChange(() => {
  try {
    if (hasAnalyticsConsent()) {
      if (memVisitorId && !localStorage.getItem(STORAGE_KEY)) localStorage.setItem(STORAGE_KEY, memVisitorId);
      if (memSessionId && !sessionStorage.getItem(SESSION_KEY)) sessionStorage.setItem(SESSION_KEY, memSessionId);
    } else {
      memVisitorId = localStorage.getItem(STORAGE_KEY) ?? memVisitorId;
      memSessionId = sessionStorage.getItem(SESSION_KEY) ?? memSessionId;
      localStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(SESSION_KEY);
    }
  } catch {
    /* ignore */
  }
});
